import { XCircleIcon } from '@heroicons/react/24/solid';
import { Suspense } from 'react';
/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 *
 * Note: Ideally this would be extracted to a shared component, but keeping
 * it local for now to match the pattern in index.tsx.
 */
import { Component, ErrorInfo, ReactNode } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
  SettingContentCard,
  SettingSectionHeader,
} from '@/sections/settings/components';
import { ScanSettingsCards } from '@/sections/settings/components';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ScanSettingsTab error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

/**
 * ScanSettingsTab manages security scanning configuration including
 * scan status, schedule, rate limiting, and source IP settings.
 *
 * @remarks
 * This component handles:
 * 1. **Scan Status** - Current scan state (active/paused) with next run time
 * 2. **Scan Header** - Unique identifier included in all scans (MD5 hash)
 * 3. **Source IP Configuration** - Static vs dynamic IP address selection
 * 4. **Scan Configuration** - Scan levels, schedules, and rate limiting
 *
 * Features:
 * - Real-time scan status monitoring
 * - Scan pause/resume controls via JobsScanStatus
 * - Static IP address whitelisting capability
 * - Dynamic IP address rotation (default)
 * - Scan level configuration (light/medium/heavy)
 * - Scan schedule management (days, time windows)
 * - Rate limiting controls (simultaneous hosts, request rates)
 *
 * Security:
 * - Scan header (MD5 hash) identifies Chariot scans for whitelisting
 * - Static IP option can be disabled via feature flag
 * - All configuration changes audited
 *
 * React 19 Features:
 * - Suspense: Progressive loading for async scan settings data
 * - ErrorBoundary: Graceful error handling for API failures
 *
 * @example
 * ```tsx
 * // Used in Settings page tabs
 * {selectedTab === 'scanSettings' && <ScanSettingsTab />}
 * ```
 *
 * @see {@link JobsScanStatus} Scan status controls
 * @see {@link ScanSettingsCards} Scan configuration cards
 * @see {@link useScanLevel} Scan level state management
 */
export function ScanSettingsTab() {
  const { canManageSettings } = useEntitlements();

  return (
    <>
      {/* Scan Configuration Section */}
      <div className="flex flex-col gap-6">
        <SettingSectionHeader
          title="Scan Settings"
          description="Manage scan status, header, levels, source IP addresses, schedules, and rate limiting for your scanning operations."
        />

        {/* Scan Settings Cards with Suspense and ErrorBoundary */}
        <SettingContentCard data-testid="scan-settings-card">
          <ErrorBoundary
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                <XCircleIcon className="text-chariot-status-critical size-12" />
                <div className="flex flex-col gap-2 text-center">
                  <h2 className="text-chariot-text-primary text-lg font-bold">
                    Failed to Load Scan Settings
                  </h2>
                  <p className="text-chariot-text-secondary max-w-md text-sm">
                    Unable to retrieve scan configuration. Please refresh the
                    page or contact support.
                  </p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                  <Spinner size="xl" />
                  <p className="text-chariot-text-secondary text-sm">
                    Loading scan settings...
                  </p>
                </div>
              }
            >
              <ScanSettingsCards editable={canManageSettings} />
            </Suspense>
          </ErrorBoundary>
        </SettingContentCard>
      </div>
    </>
  );
}
